import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import ServiceCard from '../components/ServiceCard';
import Testimonial from '../components/Testimonial';
import CTA from '../components/CTA';
import HeroPlatform from '../components/HeroPlatform';
import PromoVideoPip from '../components/PromoVideoPip';
import { getServices, getServicesByProvider, getServicesByCategory, getPages } from '../services/api';

// Example images for service features
const EXAMPLE_IMAGES = {
  Books: 'https://via.placeholder.com/400x300?text=Books',
  Tax: 'https://via.placeholder.com/400x300?text=Tax',
  Mailroom: 'https://via.placeholder.com/400x300?text=Mailroom',
  'Sales Tax': 'https://via.placeholder.com/400x300?text=Sales+Tax'
};

// Service content for each category
const SERVICE_CONTENT = {
  Books: {
    heading: "AI-driven, human-enhanced accounting",
    description: "Track cash flow, reconcile transactions, and manage billing with smart categorization. Our AI-enhanced system handles invoicing, bill pay, and integrates seamlessly with financial reports—keeping your books accurate and investor-ready.",
    link: "/books",
    images: [
      { src: "/images/books-1.png", alt: "Books Feature 1", className: "image-1" },
      { src: "/images/books-2.png", alt: "Books Feature 2", className: "image-2" },
      { src: "/images/books-3.png", alt: "Books Feature 3", className: "image-3" },
      { src: "/images/books-4.png", alt: "Books Feature 4", className: "image-4" }
    ]
  },
  Company: {
    heading: "Form, scale, and manage with confidence",
    description: "From incorporation to day-to-day ops, we automate your entity's back office. Get access to smart mailroom services, automated filings, and structured support throughout your company's lifecycle.",
    link: "/company",
    images: [
      { src: "/images/company-1.png", alt: "Company Feature 1", className: "image-1" },
      { src: "/images/company-2.png", alt: "Company Feature 2", className: "image-2" },
      { src: "/images/company-3.png", alt: "Company Feature 3", className: "image-3" },
      { src: "/images/company-4.png", alt: "Company Feature 4", className: "image-4" }
    ]
  },
  Compliance: {
    heading: "Stay compliant without the complexity",
    description: "We use AI to track filing deadlines, flag risks, and maintain up-to-date regulatory records. Covering everything from registered agent services to dissolution, sales tax, federal filings, and financial reporting—all in one dashboard.",
    link: "/compliance",
    images: [
      { src: "/images/compliance-1.png", alt: "Compliance Feature 1", className: "image-1" },
      { src: "/images/compliance-2.png", alt: "Compliance Feature 2", className: "image-2" },
      { src: "/images/compliance-3.png", alt: "Compliance Feature 3", className: "image-3" },
      { src: "/images/compliance-4.png", alt: "Compliance Feature 4", className: "image-4" }
    ]
  },
  People: {
    heading: "Smarter payroll and team operations",
    description: "Onboard talent, manage payroll, and run recruiting ops with ease. AI simplifies withholdings, compliance, and reporting while keeping your HR stack founder-friendly and scalable.",
    link: "/people",
    images: [
      { src: "/images/people-1.png", alt: "People Feature 1", className: "image-1" },
      { src: "/images/people-2.png", alt: "People Feature 2", className: "image-2" },
      { src: "/images/people-3.png", alt: "People Feature 3", className: "image-3" },
      { src: "/images/people-4.png", alt: "People Feature 4", className: "image-4" }
    ]
  },
  Tax: {
    heading: "End-to-end tax done right—with AI at the core",
    description: "We streamline tax filing, R&D credit tracking, SALT (State & Local Taxes), and real-time deduction detection. Whether it's quarterly or year-end, our tools keep you compliant and optimized for savings.",
    link: "/tax",
    images: [
      { src: "/images/tax-1.png", alt: "Tax Feature 1", className: "image-1" },
      { src: "/images/tax-2.png", alt: "Tax Feature 2", className: "image-2" },
      { src: "/images/tax-3.png", alt: "Tax Feature 3", className: "image-3" },
      { src: "/images/tax-4.png", alt: "Tax Feature 4", className: "image-4" }
    ]
  },
  "Equity Management": {
    heading: "Modern cap table & ownership management",
    description: "Issue shares, manage capitalization tables, and automate equity tracking. From SAFEs to structured fund administration, our AI tools help you stay compliant and raise with confidence.",
    link: "/equity",
    images: [
      { src: "/images/equity-management-1.png", alt: "Equity Management Feature 1", className: "image-1" },
      { src: "/images/equity-management-2.png", alt: "Equity Management Feature 2", className: "image-2" },
      { src: "/images/equity-management-3.png", alt: "Equity Management Feature 3", className: "image-3" },
      { src: "/images/equity-management-4.png", alt: "Equity Management Feature 4", className: "image-4" }
    ]
  },
  "Equity": {
    heading: "Equity and Fund management",
    description: "Issue shares, manage capitalization tables, and automate equity tracking. From SAFEs to structured fund administration, we help you stay compliant and raise with confidence. We are trained to use tools like Carta and Carta Prime to manage your equity and fund administration.",
    link: "/equity",
    images: [
      { src: "/images/equity-1.png", alt: "Equity Feature 1", className: "image-1" },
      { src: "/images/equity-2.png", alt: "Equity Feature 2", className: "image-2" },
      { src: "/images/equity-3.png", alt: "Equity Feature 3", className: "image-3" },
      { src: "/images/equity-4.png", alt: "Equity Feature 4", className: "image-4" }
    ]
  }
};

function Home() {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [pageContent, setPageContent] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const [activeService, setActiveService] = useState('Books');
  const [animatingImages, setAnimatingImages] = useState([]);
  const [missingImages, setMissingImages] = useState({});
  const [isInView, setIsInView] = useState(false);
  
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const serviceImagesRef = useRef(null);
  const observerRef = useRef(null);

  // Fetch all services and page content on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch services
        const servicesData = await getServices();
        setServices(servicesData);
        setFilteredServices(servicesData);
        
        // Extract unique providers and categories
        const uniqueProviders = [...new Set(servicesData.map(service => service.provider))];
        const uniqueCategories = [...new Set(servicesData.map(service => service.category))]
          .filter(category => category !== 'Books1'); // Filter out 'Books1' category
        setProviders(uniqueProviders);
        setCategories(uniqueCategories);
        
        // Fetch home page content
        const pagesData = await getPages();
        const homePage = pagesData.find(page => page.name === 'home') || { title: 'Welcome', content: 'Loading content...' };
        setPageContent(homePage);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Update CSS variables based on scroll position
  useEffect(() => {
    // Calculate opacity for Hero section (fades out when scrolling)
    const heroOpacity = Math.max(0, 1 - scrollY / 300);
    
    // Calculate scale for Hero section (shrinks when scrolling)
    const heroScale = Math.max(0.8, 1 - (scrollY / 600));
    
    // Calculate opacity for Services section (fades in when scrolling)
    const servicesOpacity = Math.min(1, Math.max(0, (scrollY - 200) / 300));

    // Set CSS variables
    document.documentElement.style.setProperty('--hero-opacity', heroOpacity);
    document.documentElement.style.setProperty('--hero-scale', heroScale);
    document.documentElement.style.setProperty('--services-opacity', servicesOpacity);
    
    // Disable pointer events on hero container when mostly faded out
    const heroElement = heroRef.current;
    if (heroElement) {
      if (heroOpacity < 0.1) {
        heroElement.style.pointerEvents = 'none';
      } else {
        heroElement.style.pointerEvents = 'auto';
      }
    }
  }, [scrollY]);

  // Set up Intersection Observer for scroll-based animation
  useEffect(() => {
    if (serviceImagesRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          setIsInView(entry.isIntersecting);
          
          // If coming into view and no animations in progress, start animations
          if (entry.isIntersecting && animatingImages.length === 0) {
            startSequentialAnimation();
          }
        },
        { threshold: 0.2 } // Trigger when 20% of the element is visible
      );
      
      observerRef.current.observe(serviceImagesRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [serviceImagesRef.current, animatingImages.length]);

  // Handle hash navigation
  useEffect(() => {
    // Check if there's a hash in the URL
    if (window.location.hash === '#services') {
      // Add a small delay to ensure the page is fully loaded
      setTimeout(() => {
        const servicesSection = document.getElementById('services');
        if (servicesSection) {
          // Add offset to prevent the top of services section from being cut off
          const yOffset = -20; // 20px offset from the top
          const y = servicesSection.getBoundingClientRect().top + window.scrollY + yOffset;
          
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }, 500); // Increased timeout for more reliable scrolling
    }
  }, []);

  // Listen for hash changes in case user uses browser navigation
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#services') {
        setTimeout(() => {
          const servicesSection = document.getElementById('services');
          if (servicesSection) {
            const yOffset = -20;
            const y = servicesSection.getBoundingClientRect().top + window.scrollY + yOffset;
            
            window.scrollTo({
              top: y,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Start sequential animation
  const startSequentialAnimation = () => {
    // Only start animation if element is in view
    if (!isInView) return;
    
    // Reset all animations first
    setAnimatingImages([]);
    
    // Start with the first image after a short delay
    setTimeout(() => {
      setAnimatingImages(['image-1']);
      
      // After 600ms, add the second image
      setTimeout(() => {
        setAnimatingImages(prev => [...prev, 'image-2']);
        
        // After another 600ms, add the third image
        setTimeout(() => {
          setAnimatingImages(prev => [...prev, 'image-3']);
          
          // After another 600ms, add the fourth image
          setTimeout(() => {
            setAnimatingImages(prev => [...prev, 'image-4']);
          }, 250);
        }, 250);
      }, 250);
    }, 150);
  };

  // Handle filter changes
  const handleFilterChange = async (filterType, filterValue) => {
    try {
      setLoading(true);
      setAnimatingImages([]); // Reset animations
      let filteredData;
      
      if (filterType === 'category') {
        filteredData = await getServicesByCategory(filterValue);
        setActiveFilter(`category-${filterValue}`);
        setActiveService(filterValue);
      } else if (filterType === 'provider') {
        filteredData = await getServicesByProvider(filterValue);
        setActiveFilter(`provider-${filterValue}`);
      }
      
      setFilteredServices(filteredData);
      setLoading(false);
      
      // Will start animation if in view
      if (isInView) {
        startSequentialAnimation();
      }
    } catch (error) {
      console.error('Error filtering services:', error);
      setLoading(false);
    }
  };

  // Start animation on initial load if in view
  useEffect(() => {
    if (filteredServices.length > 0 && animatingImages.length === 0 && isInView) {
      startSequentialAnimation();
    }
  }, [filteredServices, isInView]);

  // Set default category filter when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && activeFilter === 'all') {
      handleFilterChange('category', categories[0]);
    }
  }, [categories]);

  // Reset missing images when service changes
  useEffect(() => {
    setMissingImages({});
  }, [activeService]);
  
  // Handle image load error
  const handleImageError = (imageSrc) => {
    setMissingImages(prev => ({
      ...prev,
      [imageSrc]: true
    }));
  };

  if (loading && services.length === 0) {
    return <div className="loading">Loading services...</div>;
  }

  return (
    <div className="container">
      <PromoVideoPip />
      <div ref={heroRef} className="hero-container">
        <Hero title={pageContent.title} content={pageContent.content} />
        <div 
          className="scroll-arrow"
          onClick={() => window.scrollTo({
            top: window.innerHeight,
            behavior: 'smooth'
          })}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M12 17.414L3.293 8.707a1 1 0 011.414-1.414L12 14.586l7.293-7.293a1 1 0 011.414 1.414L12 17.414z"/>
          </svg>
        </div>
      </div>
      
      <div className="spacer">
        {/* Spacer div to push content below the fold */}
      </div>
      
      <HeroPlatform />
      
      <div ref={servicesRef} id="services" className="services-section">
        
        {/* Filter Controls */}
        <div className="filter-controls">
          {/* Category filters */}
          {categories.map(category => (
            <button 
              key={`category-${category}`}
              className={`filter-button ${activeFilter === `category-${category}` ? 'active' : ''}`}
              onClick={() => handleFilterChange('category', category)}
            >
              {category}
            </button>
          ))}
        </div>
        
        {/* Services Content */}
        {filteredServices.length > 0 ? (
          <div className="service-content-container">
            <div className="service-text">
              <h2 className="service-heading">{SERVICE_CONTENT[activeService]?.heading || "Service Information"}</h2>
              <p className="service-description">{SERVICE_CONTENT[activeService]?.description || "Description not available."}</p>
              <Link to={SERVICE_CONTENT[activeService]?.link || "#"} className="learn-more-link">
                Learn more 
                <img src="/images/arrow.svg" alt="Arrow" className="arrow-icon" />
              </Link>
            </div>
            <div className="service-image-grid" ref={serviceImagesRef}>
              {SERVICE_CONTENT[activeService]?.images.map((image, index) => (
                !missingImages[image.src] && (
                  <div 
                    key={index} 
                    className={`service-image ${image.className} ${animatingImages.includes(image.className) ? 'animate-in' : ''}`}
                  >
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className="service-content-image"
                      onError={() => handleImageError(image.src)}
                    />
                  </div>
                )
              ))}
            </div>
          </div>
        ) : (
          <div className="no-services">No services found in this category.</div>
        )}
      </div>
      
      {/* Testimonials Section */}
      {/* <div className="testimonials-section">
        <h2 className="testimonials-heading">Here's what our customers love about us</h2>
        <div className="testimonials-container">
          <Testimonial 
            quote="Inkle went above and beyond, offering prompt assistance. Highly recommended this for all startups looking for cost-effective solutions with exceptional customer support."
            name="Ryan Wenger"
            title="Co-Founder"
            company="Inhouse"
            photo="/images/testimonials/i-1.png"
            bgColor="#a5c5f9"
            socialIcon="linkedin"
          />
          <Testimonial 
            quote="Incredible software & an extremely detail-oriented team. Must have for founders wanting to save time without sacrificing quality."
            name="Clayton Jacobs"
            title="Founder & CEO"
            company="CreatorDB"
            photo="/images/testimonials/i-2.png"
            bgColor="#c9e5c9"
            socialIcon="linkedin"
          />
          <Testimonial 
            quote="Nothing comes close to what Inkle offers for US Taxes and Compliance. Their product has a simple and easy-to-use UI with streamlined processes."
            name="Nikolay Bonev"
            title="Co-Founder & CTO"
            company="Shelf"
            photo="/images/testimonials/i-3.png"
            bgColor="#fff0a8"
            socialIcon="linkedin"
          />
          <Testimonial 
            quote="Inkle is a must-have for founders with US entities. They offer amazing customer service and results at a very affordable price."
            name="Fernando Braun"
            title="Co-Founder & CEO"
            company="2250"
            photo="/images/testimonials/i-4.png"
            bgColor="#ffd1dc"
            socialIcon="linkedin"
          />
        </div>
      </div>
       */}
      
      {/* Features Section */}
      <div className="features-section">
        <ul className="features-list">
          <li className="feature-item">
            <span className="feature-bullet">■</span>
            <div className="feature-content">
              <h3 className="feature-title">Dedicated <span className="highlight">Account Manager</span></h3>
              <p className="feature-description">Get <span className="highlight">personalized support</span> from a real expert who understands your business and stays with you as you grow.</p>
            </div>
          </li>
          <li className="feature-item">
            <span className="feature-bullet">■</span>
            <div className="feature-content">
              <h3 className="feature-title">Use our platform or we use yours</h3>
              <p className="feature-description">Whether you're on <span className="highlight">QuickBooks</span>, <span className="highlight">Xero</span>, <span className="highlight">Carta</span>, or another tool—we integrate seamlessly or work directly inside your system.</p>
            </div>
          </li>
          <li className="feature-item">
            <span className="feature-bullet">■</span>
            <div className="feature-content">
              <h3 className="feature-title"><span className="highlight">Custom workflows</span> for your team</h3>
              <p className="feature-description">We tailor reporting, approvals, and task tracking to match how your team works. 
                Chat with us via <span className="highlight">WhatsApp</span>, <span className="highlight">Slack</span>, <span className="highlight">Teams</span>, <span className="highlight">email</span>, or other in-app messaging and stay in sync without switching tools.</p>
            </div>
          </li>
          <li className="feature-item">
            <span className="feature-bullet">■</span>
            <div className="feature-content">
              <h3 className="feature-title">Built-in <span className="highlight">AI</span> and <span className="highlight">automation</span></h3>
              <p className="feature-description">Let our tools handle the heavy lifting with smart categorization, real-time syncing, and automated reminders that keep you ahead of deadlines.</p>
            </div>
          </li>
        </ul>
      </div>
      
      <CTA />
    </div>
  );
}

export default Home;